import { useMemo } from "react";
import { Check } from "lucide-react";
import type { Phase } from "@/lib/types";
import { cn } from "@/lib/utils";

// Canonical launch spine — the top-level story of the quarter.
// Every other row lives on the Gates & Dates page; this is the poster-view.
const SPINE: { phaseId: string; gateBeforeId?: string; displayName?: string; displayDate?: string }[] = [
  { phaseId: "alpha" },
  { phaseId: "alpha_plus", gateBeforeId: "gate1" },
  { phaseId: "closed_beta_1", gateBeforeId: "gate2", displayName: "Closed Beta 1" },
  { phaseId: "closed_beta_2", gateBeforeId: "gate3", displayName: "Closed Beta 2" },
  { phaseId: "ga", gateBeforeId: "gate4" },
];

const GATE_SHORT: Record<string, string> = {
  gate1: "Gate 1",
  gate2: "Gate 2",
  gate3: "Gate 3",
  gate4: "Gate 4",
};

function statusTone(status: Phase["status"] | undefined) {
  if (status === "complete") return "complete";
  if (status === "active") return "active";
  return "upcoming";
}

export function TimelineSpine({ phases }: { phases: Phase[] }) {
  const items = useMemo(() => {
    const byId = new Map(phases.map((p) => [p.id, p]));
    return SPINE.map((s) => {
      const phase = byId.get(s.phaseId);
      const gate = s.gateBeforeId ? byId.get(s.gateBeforeId) : undefined;
      return { phase, gate, displayName: s.displayName, displayDate: s.displayDate, key: s.phaseId };
    });
  }, [phases]);

  const n = items.length;
  const activeIdx = items.findIndex((it) => it.phase?.status === "active");

  // A gate between items[i-1] and items[i] is considered crossed once the
  // phase on either side is no longer "upcoming" — that is, we've entered
  // or finished the phase that the gate was the door to.
  const isGateCrossed = (idx: number): boolean => {
    const g = items[idx]?.gate;
    if (!g) return false;
    if (g.status === "complete") return true;
    const prev = items[idx - 1]?.phase?.status;
    const curr = items[idx]?.phase?.status;
    return prev === "complete" || curr === "active" || curr === "complete";
  };

  // Green progress line extends to the furthest completed milestone —
  // end of a completed phase segment, or the tick of a crossed gate.
  const progressPct = useMemo(() => {
    let pct = 0;
    items.forEach((it, i) => {
      if (it.phase?.status === "complete") pct = Math.max(pct, ((i + 1) / n) * 100);
      if (isGateCrossed(i)) pct = Math.max(pct, (i / n) * 100);
    });
    return pct;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, n]);

  return (
    <div className="py-3">
      <div className="relative">
        {/* Top gate-labels row */}
        <div
          className="grid items-end"
          style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}
        >
          {items.map((it) => (
            <div key={`g-${it.key}`} className="relative flex min-h-[44px] items-end">
              {it.gate && (
                <div className="absolute left-0 -translate-x-1/2 pb-2 text-center leading-tight">
                  <div className="text-micro uppercase tracking-wider text-muted-foreground">
                    {GATE_SHORT[it.gate.id] ?? "Gate"}
                  </div>
                  <div className="text-caption font-semibold tabular text-foreground">
                    {it.gate.date}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Spine line + active glow + progress fill + gate ticks */}
        <div className="relative h-[2px] w-full bg-border">
          {/* Active-phase coral glow across just the active segment */}
          {activeIdx >= 0 && (
            <div
              className="pointer-events-none absolute top-1/2 h-[12px] -translate-y-1/2 rounded-full bg-gradient-to-r from-primary/0 via-primary/60 to-primary/0 blur-[2px]"
              style={{
                left: `${(activeIdx / n) * 100}%`,
                width: `${(1 / n) * 100}%`,
              }}
              aria-hidden
            />
          )}

          {/* Emerald progress line up to the last completed milestone */}
          {progressPct > 0 && (
            <div
              className="absolute left-0 top-1/2 h-[2px] -translate-y-1/2 bg-emerald-500"
              style={{ width: `${progressPct}%` }}
              aria-hidden
            />
          )}

          {/* Gate ticks at each phase boundary (skip the first cell — no gate before Alpha) */}
          <div
            className="absolute inset-0 grid"
            style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}
          >
            {items.map((it, idx) => (
              <div key={`t-${it.key}`} className="relative">
                {idx > 0 && (
                  isGateCrossed(idx) ? (
                    <div
                      className="absolute left-0 top-1/2 flex h-4 w-4 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-emerald-500 text-white shadow-sm ring-2 ring-background"
                      aria-label="Gate complete"
                    >
                      <Check size={10} strokeWidth={3.5} />
                    </div>
                  ) : (
                    <div
                      className={cn(
                        "absolute left-0 top-1/2 h-3 w-[2px] -translate-x-1/2 -translate-y-1/2 rounded",
                        idx === activeIdx ? "bg-primary" : "bg-muted-foreground/50"
                      )}
                    />
                  )
                )}
              </div>
            ))}
            {/* End cap tick at very right (after GA) */}
            <div
              className="absolute right-0 top-1/2 h-3 w-[2px] translate-x-1/2 -translate-y-1/2 rounded bg-muted-foreground/50"
              aria-hidden
            />
          </div>
        </div>

        {/* Phase labels row */}
        <div
          className="mt-4 grid"
          style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}
        >
          {items.map((it, idx) => {
            const tone = statusTone(it.phase?.status);
            const name = it.displayName ?? it.phase?.name ?? "—";
            const date = it.displayDate ?? it.phase?.date ?? "";
            return (
              <div key={`p-${it.key}`} className="px-2 text-center leading-tight">
                <div
                  className={cn(
                    "text-body font-semibold",
                    tone === "active" && "text-primary",
                    tone === "complete" && "text-emerald-700 dark:text-emerald-400",
                    tone === "upcoming" && "text-muted-foreground"
                  )}
                >
                  {name}
                </div>
                <div
                  className={cn(
                    "mt-0.5 text-micro tabular",
                    tone === "active" || idx === activeIdx ? "text-foreground/80" : "text-muted-foreground"
                  )}
                >
                  {date}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
